/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import ErrorScreen, { ERROR_CONFIGS } from '../components/ErrorScreen';

// All error messages/titles are defined in ErrorScreen.jsx → ERROR_CONFIGS.
// To update any error message, edit that file only.

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
};

const clearCookie = (name) => {
  document.cookie = `${name}=; Max-Age=0; path=/`;
};

const ErrorPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [type] = useState(() => {
    // 1. Router state — set by in-app navigate() calls (e.g. Capture.jsx)
    if (state?.type) {
      sessionStorage.setItem('error_type', state.type);
      if (state?.message) {
        sessionStorage.setItem('error_message', state.message);
      }
      return state.type;
    }

    // 2. Query parameter — set by server redirect e.g. /error?type=deleted
    const queryType = searchParams.get('type');
    if (queryType) {
      sessionStorage.setItem('error_type', queryType);
      return queryType;
    }

    // 3. Cookie — legacy fallback
    const cookieType = getCookie('error_type');
    if (cookieType) {
      sessionStorage.setItem('error_type', cookieType);
      clearCookie('error_type');
      return cookieType;
    }

    // 4. sessionStorage — survives a page refresh on /error
    const stored = sessionStorage.getItem('error_type');
    if (stored) { return stored; }

    return 'default';
  });

  // Resolve the "from" path to redirect back to
  const [from] = useState(() => {
    // 1. Router state
    if (state?.from) {
      sessionStorage.setItem('error_from', state.from);
      return state.from;
    }
    // 2. Query parameter
    const queryFrom = searchParams.get('from');
    if (queryFrom) {
      sessionStorage.setItem('error_from', queryFrom);
      return queryFrom;
    }
    // 3. sessionStorage fallback
    return sessionStorage.getItem('error_from') || '/';
  });

  // Resolve config from the central ERROR_CONFIGS map
  const config = ERROR_CONFIGS[type] ?? ERROR_CONFIGS.default;

  // Custom message from state (used for session errors with specific messages)
  const customMessage = state?.message || sessionStorage.getItem('error_message');
  const displayMessage = customMessage || config.message;

  // Branding passed via router state (available when navigating from Capture.jsx)
  const branding = state?.branding;

  // Clear custom message when component unmounts
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('error_message');
    };
  }, []);

  // Auto-redirect when back online if it was an offline error
  useEffect(() => {
    if (type === 'offline') {
      const handleOnline = () => {
        sessionStorage.removeItem('error_type');
        sessionStorage.removeItem('error_from');
        navigate(from, { replace: true });
      };

      // 1. Check if already online right now
      if (navigator.onLine) {
        handleOnline();
        return;
      }

      // 2. Otherwise listen for the status to change
      window.addEventListener('online', handleOnline);
      return () => window.removeEventListener('online', handleOnline);
    }
  }, [type, from, navigate]);

  const handleRetry = () => {
    // If offline, check navigator.onLine before redirecting
    if (type === 'offline' && !navigator.onLine) {
      return; // Still offline, do nothing or show a toast
    }
    sessionStorage.removeItem('error_type');
    sessionStorage.removeItem('error_from');
    sessionStorage.removeItem('error_message');
    navigate(from, { replace: true });
  };

  return (
    <ErrorScreen
      title={config.title}
      message={displayMessage}
      icon={config.icon}
      iconColor={config.iconColor}
      branding={branding}
      action={(type === 'offline' || type === 'default') ? {
        label: 'Retry',
        onClick: handleRetry
      } : null}
    />
  );
};

export default ErrorPage;

